import React, {useRef, useEffect, useState} from "react";
import ReactDOM from "react-dom";
import {
  FaTimesCircle,
  FaSearch,
  FaCode,
  FaRedo,
  FaFilter,
  FaSort,
  FaCheck,
  FaChevronDown,
} from "react-icons/fa";
import ContextMenu from "./ContextMenu";

const SearchBox = ({
                     value: propValue,
                     onSearch,
                     placeholder = "Search ...",
                     autoFocus = true,
                     eventTarget = null,
                     showRefresh = false,
                     onRefresh = () => {
                     },
                     filterOptions = [],
                     sortOptions = [],
                     activeFilter = null,
                     activeSort = null,
                     onFilterClear = null,
                     onSortClear = null,
                     includeRegexOption = false
                   }) => {
  const inputRef = useRef(null);
  const filterButtonRef = useRef(null);
  const sortButtonRef = useRef(null);
  const [isRegexMode, setIsRegexMode] = useState(() => {
    return localStorage.getItem("search-regex-mode") === "true";
  });
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [filterMenuPosition, setFilterMenuPosition] = useState({x: 0, y: 0});
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortMenuPosition, setSortMenuPosition] = useState({x: 0, y: 0});

  // Use local state if no value prop is provided
  const [localValue, setLocalValue] = useState("");
  const value = propValue !== undefined ? propValue : localValue;

  const handleSetSearchBoxTerm = (e) => {
    onSearch(e.detail.searchTerm, e.detail.isRegexMode);
  };

  useEffect(() => {
    window.addEventListener("setSearchBoxTerm", handleSetSearchBoxTerm);
    return () =>
      window.removeEventListener("setSearchBoxTerm", handleSetSearchBoxTerm);
  }, []);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [autoFocus]);

  const handleChange = (event) => {
    const newValue = event.target.value;
    if (propValue === undefined) {
      setLocalValue(newValue);
    }
    onSearch(newValue, isRegexMode);
  };

  const handleClear = () => {
    if (propValue === undefined) {
      setLocalValue("");
    }
    onSearch("", isRegexMode);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && eventTarget) {
      window.dispatchEvent(
        new CustomEvent("searchBoxEnter", {
          detail: {target: eventTarget, value: value},
        }),
      );
    }
  };

  const toggleSearchMode = () => {
    const newMode = !isRegexMode;
    setIsRegexMode(newMode);
    localStorage.setItem("search-regex-mode", newMode);
    onSearch(value, newMode);
  };

  const handleFilterClick = (event) => {
    event.preventDefault();
    const rect = filterButtonRef.current.getBoundingClientRect();
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;
    setFilterMenuPosition({
      x: rect.left + scrollX,
      y: rect.bottom + scrollY + 5,
    });
    setShowFilterMenu(!showFilterMenu);
    setShowSortMenu(false);
  };

  const handleFilterSelect = (option) => {
    if (option.onSelect) {
      option.onSelect();
    }
    setShowFilterMenu(false);
  };

  const handleSortClick = (event) => {
    event.preventDefault();
    const rect = sortButtonRef.current.getBoundingClientRect();
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;
    setSortMenuPosition({
      x: rect.left + scrollX,
      y: rect.bottom + scrollY + 5,
    });
    setShowSortMenu(!showSortMenu);
    setShowFilterMenu(false);
  };

  const handleSortSelect = (option) => {
    if (option.onSelect) {
      option.onSelect();
    }
    setShowSortMenu(false);
  };

  const handleFilterClear = (e) => {
    e.stopPropagation();
    if (onFilterClear) {
      onFilterClear();
    }
  };

  const handleSortClear = (e) => {
    e.stopPropagation();
    if (onSortClear) {
      onSortClear();
    }
  };

  const filterMenuItems = filterOptions.map((option) => ({
    label: option.label,
    onClick: () => handleFilterSelect(option),
    tooltip: option.tooltip || option.label,
    isActive: activeFilter && activeFilter.value === option.value,
    renderItem: (item) => (
      <div className="flex items-center justify-between w-full">
        <span>{item.label}</span>
        {item.isActive && <FaCheck className="ml-2 text-green-500"/>}
      </div>
    ),
  }));

  const sortMenuItems = sortOptions.map((option) => ({
    label: option.label,
    onClick: () => handleSortSelect(option),
    tooltip: option.tooltip || option.label,
    isActive: activeSort && activeSort.value === option.value,
    renderItem: (item) => (
      <div className="flex items-center justify-between w-full">
        <span>{item.label}</span>
        {item.isActive && <FaCheck className="ml-2 text-green-500"/>}
      </div>
    ),
  }));

  return (
    <div className="relative w-full flex items-center space-x-2">
      {showRefresh && (
        <button
          onClick={onRefresh}
          className="p-2 rounded-full bg-gray-700 text-gray-400 hover:text-white"
          title="Refresh data"
        >
          <FaRedo size={20}/>
        </button>
      )}
      <div className="relative flex-grow">
        {includeRegexOption ?
          <button
            onClick={toggleSearchMode}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
            title={
              isRegexMode ? "Switch to normal search" : "Switch to regex search"
            }
          >
            {isRegexMode ? <FaCode size={16}/> : <FaSearch size={16}/>}
          </button>
          :
          <span
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
          >
           <FaSearch size={16}/>
          </span>
        }
        <input
          ref={inputRef}
          type="text"
          value={value || ""}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={
            (isRegexMode && includeRegexOption)
              ? placeholder.replace("...", "") + "(regex) ..."
              : placeholder
          }
          className="w-full p-2 pl-10 pr-10 rounded-full bg-gray-700 text-white placeholder-gray-400"
        />
        {value && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
          >
            <FaTimesCircle size={20}/>
          </button>
        )}
      </div>

      {filterOptions.length > 0 && (
        <div className="relative">
          <button
            ref={filterButtonRef}
            onClick={handleFilterClick}
            className={`flex items-center space-x-2 p-2 rounded-lg ${
              activeFilter
                ? "bg-blue-600 text-white pr-3"
                : "bg-gray-700 text-gray-400 hover:text-white rounded-full"
            }`}
            title={activeFilter ? activeFilter.label : "Filter options"}
          >
            <FaFilter size={activeFilter ? 16 : 20}/>
            {activeFilter && (
              <>
                <span className="ml-1 text-sm truncate max-w-[100px]">
                  {activeFilter.label}
                </span>
                <div className="flex items-center">
                  <FaChevronDown size={12} className="mr-1"/>
                  <span
                    onClick={handleFilterClear}
                    className="text-white hover:text-red-300"
                    title="Clear filter"
                  >
                    <FaTimesCircle size={14}/>
                  </span>
                </div>
              </>
            )}
          </button>
          {showFilterMenu &&
            ReactDOM.createPortal(
              <div
                style={{
                  position: "absolute",
                  left: `${filterMenuPosition.x}px`,
                  top: `${filterMenuPosition.y}px`,
                  zIndex: 9999,
                }}
              >
                <ContextMenu
                  items={filterMenuItems}
                  x={filterMenuPosition.x}
                  y={filterMenuPosition.y}
                  onClose={() => setShowFilterMenu(false)}
                  renderItem={(item) =>
                    item.renderItem && item.renderItem(item)
                  }
                />
              </div>,
              document.body,
            )}
        </div>
      )}

      {sortOptions.length > 0 && (
        <div className="relative">
          <button
            ref={sortButtonRef}
            onClick={handleSortClick}
            className={`flex items-center space-x-2 p-2 rounded-lg ${
              activeSort
                ? "bg-blue-600 text-white pr-3"
                : "bg-gray-700 text-gray-400 hover:text-white rounded-full"
            }`}
            title={activeSort ? activeSort.label : "Sort options"}
          >
            <FaSort size={activeSort ? 16 : 20}/>
            {activeSort && (
              <>
                <span className="ml-1 text-sm truncate max-w-[100px]">
                  {activeSort.label}
                </span>
                <div className="flex items-center">
                  <FaChevronDown size={12} className="mr-1"/>
                  <span
                    onClick={handleSortClear}
                    className="text-white hover:text-red-300"
                    title="Reset to default sort"
                  >
                    <FaTimesCircle size={14}/>
                  </span>
                </div>
              </>
            )}
          </button>
          {showSortMenu &&
            ReactDOM.createPortal(
              <div
                style={{
                  position: "absolute",
                  left: `${sortMenuPosition.x}px`,
                  top: `${sortMenuPosition.y}px`,
                  zIndex: 9999,
                }}
              >
                <ContextMenu
                  items={sortMenuItems}
                  x={sortMenuPosition.x}
                  y={sortMenuPosition.y}
                  onClose={() => setShowSortMenu(false)}
                  renderItem={(item) =>
                    item.renderItem && item.renderItem(item)
                  }
                />
              </div>,
              document.body,
            )}
        </div>
      )}
    </div>
  );
};

export default SearchBox;
